import { useState, useEffect, useContext } from 'react';
import { AuthUserContext, withAuthorization } from '../Session';
import AdminLayout from './AdminLayout';
import { Skeleton, Tabs, Alert, Button } from 'antd';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';

import { useCompany } from '../../swr';
import { useRecoilState } from 'recoil';
import { restaurantDetailsState, activeRestaurantState } from '../../recoil';
import { useBookingValidation } from './Restaurant/RestaurantBookingsValidation';
import RestaurantBookings from './Restaurant/RestaurantBookings';
import RestaurantBookingSettings from './Restaurant/RestaurantBookingSettings';
import RestaurantBookingEmbed from './Restaurant/RestaurantBookingEmbed';
import Onboarding from './Onboarding';

const { TabPane } = Tabs;

function RestaurantBookingsHome(props) {

    // Get AuthUser ID from Firebase
    const firebaseAuthUser = useContext(AuthUserContext);
    // Get URL parameters
    const { restaurantId, bookingId } = useParams();
    const location = useLocation();

    const [activeTabKey, setActiveTabKey] = useState();
    const [activeRestaurant, setActiveRestaurant] = useRecoilState(activeRestaurantState);

    // Use the existing booking validation hook to get booking settings
    const {
        bookingSettings,
        loading: loadingBookingSettings,
        error: bookingSettingsError
    } = useBookingValidation(activeRestaurant?.reservationShortUrl);

    const { data: companyDetails, isLoading: isLoadingCompanyDetails } = useCompany('getDetails', firebaseAuthUser.uid);
    let companyIdentificationToken = null;

    if (companyDetails) {
        companyIdentificationToken = companyDetails.uniqueIdentificationToken;
    }

    const routeLocation = useLocation();
    const navigate = useNavigate();

    // Check if booking settings exist and are properly configured
    const hasBookingSettings = bookingSettings && 
        Object.keys(bookingSettings).length > 0 && 
        bookingSettings.days &&
        Object.keys(bookingSettings.days).length > 0;

    // Handle tab changes and redirect logic
    const callbackTabs = (key) => {
        setActiveTabKey(key);
        navigate(key);
    };

    // Handle redirect to settings
    const redirectToSettings = () => {
        setActiveTabKey('#settings');
        navigate('#settings');
    };

    useEffect(() => {
        if (routeLocation.hash) {
            setActiveTabKey(routeLocation.hash);
        } else {
            // If no booking settings exist, default to settings tab
            if (!loadingBookingSettings && !hasBookingSettings) {
                setActiveTabKey('#settings');
            } else {
                setActiveTabKey('#list');
            }
        }
    }, [routeLocation.hash, loadingBookingSettings, hasBookingSettings]);

    // Auto-redirect to settings if no settings exist and user tries to access other tabs
    useEffect(() => {
        if (!loadingBookingSettings && !hasBookingSettings && 
            activeTabKey && activeTabKey !== '#settings') {
            redirectToSettings();
        }
    }, [loadingBookingSettings, hasBookingSettings, activeTabKey]);

    return (
        <AuthUserContext.Consumer>
            {(authUser) => (
                <AdminLayout authUser={authUser} pageTitle={activeRestaurant?.title || ''}>
                    <h2>Rezervări</h2>
                    {isLoadingCompanyDetails || loadingBookingSettings ? (
                        <Skeleton active></Skeleton>
                    ) : companyDetails.message ? (
                        <Onboarding />
                    ) : (
                        <>
                            {/* Show setup message if no booking settings exist */}
                            {!hasBookingSettings && (
                                <Alert
                                    message="Configurare necesară"
                                    description="Pentru a putea gestiona rezervările, trebuie să configurați mai întâi setările de rezervare. Vă rugăm să completați configurarea în tab-ul 'Setări rezervări'."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    
                                />
                            )}

                            <Tabs 
                                activeKey={activeTabKey} 
                                defaultActiveKey={routeLocation.hash} 
                                onChange={callbackTabs}
                            >
                                <TabPane 
                                    tab="Rezervări" 
                                    key="#list"
                                    disabled={!hasBookingSettings}
                                >
                                    {hasBookingSettings ? (
                                        <RestaurantBookings
                                            restaurantId={restaurantId || activeRestaurant?.uniqueIdentificationToken}
                                            shortUrl={activeRestaurant?.reservationShortUrl}
                                            bookingId={bookingId}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <p>Configurați mai întâi setările de rezervare pentru a putea gestiona rezervările.</p>
                                            <Button 
                                                type="primary" 
                                                icon={<SettingOutlined />}
                                                onClick={redirectToSettings}
                                            >
                                                Mergi la setări
                                            </Button>
                                        </div>
                                    )}
                                </TabPane>
                                
                                <TabPane tab="Setări rezervări" key="#settings">
                                    <RestaurantBookingSettings
                                        restaurantId={restaurantId || activeRestaurant?.uniqueIdentificationToken}
                                        shortUrl={activeRestaurant?.reservationShortUrl}
                                    />
                                </TabPane>
                                
                                <TabPane 
                                    tab="Formular rezervare" 
                                    key="#embed"
                                    disabled={!hasBookingSettings}
                                >
                                    {hasBookingSettings ? (
                                        <RestaurantBookingEmbed restaurantId={activeRestaurant.reservationShortUrl} />
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <p>Configurați mai întâi setările de rezervare pentru a putea genera formularul de rezervare.</p>
                                            <Button 
                                                type="primary" 
                                                icon={<SettingOutlined />}
                                                onClick={redirectToSettings}
                                            >
                                                Mergi la setări
                                            </Button>
                                        </div>
                                    )}
                                </TabPane>
                            </Tabs>
                        </>
                    )}
                </AdminLayout>
            )}
        </AuthUserContext.Consumer>
    );
}

const condition = (authUser) => !!authUser;
export default withAuthorization(condition)(RestaurantBookingsHome);